// ADR 6.10.26b §3 — the handoff panel, presentational only.
//
// For a browser that cannot install, hands the learner to one on the same
// device that can. Wears `InstallSteps`'s frame (`InstallCard`) and its
// contract: not modal, no portal, no focus trap. Takes `target` and
// `chromeMissing` and reads nothing else, so any surface can mount it.
//
// Three parts. The Open link (Edge on Windows, Chrome on Android) is a real
// `<a>`, not `location.assign`: launching an external scheme needs the click's
// user activation, and a link is what assistive technology announces. The
// Copy link is on every target, because Edge can be uninstalled, Chrome may be
// absent and a browser may ignore the scheme. The URL itself is selectable
// text, for when the clipboard is refused.
//
// `InstallPanel` is the one choice between the steps and this panel, so the
// strip, the account entry and the header/door button each draw it the same.
//
// Must not import the wasm facade: it is in 25.9.26f's boot graph.

import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { copy } from '../features/plans/share/shareLink'
import { watchHandoff } from '../telemetry/field/installReports'
import { emitInstallHandoffClicked } from '../telemetry/installStrip'
import { Button, buttonClassName } from '../ui/Button'
import { chromeMissing, handoffHref, installUrl, type HandoffTarget } from './handoff'
import { InstallGuide } from './guide/InstallGuide'
import { InstallCard, InstallSteps } from './InstallSteps'
import type { InstallState } from './useInstallAffordance'

const BODY: Record<HandoffTarget, string> = {
  windows_edge: 'pwa_handoff_edge_body',
  android_chrome: 'pwa_handoff_chrome_body',
  mac_copy: 'pwa_handoff_copy_body_mac',
  linux_copy: 'pwa_handoff_copy_body_linux',
}

const OPEN: Partial<Record<HandoffTarget, string>> = {
  windows_edge: 'pwa_handoff_edge_open',
  android_chrome: 'pwa_handoff_chrome_open',
}

interface InstallHandoffProps {
  target: HandoffTarget
  /** §4 — the boot-latched `handoff=missing`: Chrome's intent fell back here. */
  chromeMissing: boolean
  onClose(): void
}

export function InstallHandoff({ target, chromeMissing, onClose }: InstallHandoffProps) {
  const { t } = useTranslation()
  const origin = window.location.origin
  const missing = chromeMissing && target === 'android_chrome'
  const href = missing ? null : handoffHref(target, origin)
  const openKey = OPEN[target]
  return (
    <InstallCard title={t('pwa_handoff_title')} onClose={onClose}>
      <p className="text-body-md text-ink-muted">{t(missing ? 'pwa_handoff_chrome_missing' : BODY[target])}</p>
      <div className="flex flex-wrap items-center gap-2">
        {href !== null && openKey !== undefined && (
          <a
            href={href}
            className={buttonClassName('primary')}
            onClick={() => {
              emitInstallHandoffClicked(target, 'open')
              // ADR 15.10.26d §3 — `android_chrome` only (it ignores the rest): an ignored intent shows nothing.
              watchHandoff(target)
            }}
          >
            {t(openKey)}
          </a>
        )}
        <CopyLink target={target} url={installUrl(origin)} />
      </div>
      <p className="select-all break-all text-body-sm text-ink-muted">{installUrl(origin)}</p>
    </InstallCard>
  )
}

/** 7.9.26k's `copy()`, and its outcome as a "Link copied" line. */
function CopyLink({ target, url }: { target: HandoffTarget; url: string }) {
  const { t } = useTranslation()
  const [copied, setCopied] = useState(false)
  const onCopy = () => {
    emitInstallHandoffClicked(target, 'copy')
    void copy(url).then((outcome) => setCopied(outcome === 'copied'))
  }
  return (
    <>
      <Button variant="secondary" onClick={onCopy}>
        {t('share_copy_link')}
      </Button>
      {copied && (
        <span role="status" className="text-label-lg text-ink-muted">
          {t('share_link_copied')}
        </span>
      )}
    </>
  )
}

/** The affordances whose CTA opens a panel rather than the prompt. */
export type PanelState = Exclude<InstallState, { affordance: 'none' | 'prompt' }>

/** The platform's steps, the install guide (iOS, Mac Safari), or — for `handoff` — this panel with the boot-latched `chromeMissing()`. */
export function InstallPanel({ state, onClose }: { state: PanelState; onClose(): void }) {
  if (state.affordance === 'handoff') {
    return <InstallHandoff target={state.target} chromeMissing={chromeMissing()} onClose={onClose} />
  }
  // ADR 16.10.26f §5 — iOS and Mac Safari get the install guide (the stepper,
  // or 16.10.26b's sentence for an unguided iOS variant); narrows `state` for the steps below.
  if (state.affordance === 'ios_instructions') return <InstallGuide platform="ios" onClose={onClose} />
  if (state.affordance === 'mac_instructions') return <InstallGuide platform="mac_safari" onClose={onClose} />
  return <InstallSteps platform={state.platform} onClose={onClose} />
}
